import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import {
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { SAVING_ARENA_API_CONFIG } from '../../../../core/api/api.config';
import { ApiPagination, FrontendUser } from '../../../../core/api/api.models';
import { SavingArenaApiService } from '../../../../core/api/saving-arena-api.service';
import { SeoService } from '../../../../core/seo/seo.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../../core/seo/structured-data';
import { Breadcrumb } from '../../../../shared/breadcrumb/breadcrumb';
import { SectionHeading } from '../../../../shared/section-heading/section-heading';
import { BLOG_AUTHORS } from '../../blog.mock';

interface AuthorCard {
  slug: string;
  name: string;
  role: string;
  bio: string;
  imageUrl: string;
  articleCount: number;
  storesCovered: number;
  profilePath: string;
  searchContent: string;
}

interface AuthorQuery {
  page: number;
  searchTerm: string;
  debounce: boolean;
}

const AUTHORS_PAGE_LIMIT = 50;
const SEARCH_DEBOUNCE_MS = 450;
/** Roles are only worth showing when they say something; every seeded user is an "Author". */
const GENERIC_ROLE = 'author';
const EXPERTS_PATH = '/experts';
const EXPERTS_TITLE = 'Our Savings Experts | SavingArena';
const EXPERTS_DESCRIPTION =
  'Meet the SavingArena editorial team: the coupon researchers, deal analysts and shopping writers behind every verified offer and guide.';

@Component({
  selector: 'app-blog-authors-page',
  imports: [Breadcrumb, RouterLink, SectionHeading],
  templateUrl: './blog-authors-page.html',
  styleUrl: './blog-authors-page.scss',
})
export class BlogAuthorsPage {
  protected readonly crumbs = breadcrumbTrail({ label: 'Experts' });

  private readonly api = inject(SavingArenaApiService);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly destroyRef = inject(DestroyRef);
  private readonly seo = inject(SeoService);
  private readonly authorQueries = new Subject<AuthorQuery>();

  protected readonly authors = signal<AuthorCard[]>(
    BLOG_AUTHORS.map((author) => ({
      slug: author.slug,
      name: author.name,
      role: author.role,
      bio: this.stripHtml(author.bio),
      imageUrl: author.imageUrl,
      articleCount: author.articleCount,
      storesCovered: author.storesCovered,
      profilePath: this.expertProfilePath(author.slug),
      searchContent: this.searchContent(author.name, author.role),
    })),
  );
  protected readonly currentPage = signal(1);
  protected readonly pagination = signal<ApiPagination>(this.emptyPagination());
  protected readonly isLoadingAuthors = signal(false);
  protected readonly isSearchPending = signal(false);
  protected readonly searchTerm = signal('');

  protected readonly filteredAuthors = computed(() => this.authors());

  /** Reads "30 experts" / "1 expert" — shown in the toolbar beside the search field. */
  protected readonly resultsLabel = computed(() => {
    const total = this.pagination().total || this.filteredAuthors().length;
    return `${total} ${total === 1 ? 'expert' : 'experts'}`;
  });

  protected readonly hasPagination = computed(() => this.pagination().pages > 1);
  protected readonly pageNumbers = computed(() =>
    Array.from({ length: this.pagination().pages }, (_, index) => index + 1),
  );

  constructor() {
    this.applyExpertsSeo();
    this.listenForAuthorQueries();
    this.requestAuthors(1, false);
  }

  /**
   * The listing's copy is static, so this runs in the constructor. The canonical is pinned to
   * `/experts` because the same component is also routed at `/blogs/experts` — without it the two
   * URLs would compete as duplicates.
   */
  private applyExpertsSeo(): void {
    const url = this.seo.absolute(EXPERTS_PATH);

    this.seo.apply({
      title: EXPERTS_TITLE,
      description: EXPERTS_DESCRIPTION,
      canonicalPath: EXPERTS_PATH,
    });

    this.seo.setJsonLd([
      webPageSchema({
        origin: this.seo.origin,
        url,
        name: EXPERTS_TITLE,
        description: EXPERTS_DESCRIPTION,
      }),
      breadcrumbSchemaFor(this.crumbs, (p) => this.seo.absolute(p)),
    ]);
  }

  protected updateSearchTerm(event: Event): void {
    this.searchTerm.set((event.target as HTMLInputElement).value);
    this.requestAuthors(1, true);
  }

  protected goToPage(page: number): void {
    const totalPages = this.pagination().pages || 1;
    const nextPage = Math.min(Math.max(page, 1), totalPages);

    if (nextPage === this.currentPage()) {
      return;
    }

    this.requestAuthors(nextPage, false);
  }

  private listenForAuthorQueries(): void {
    merge(
      this.authorQueries.pipe(
        filter((query) => query.debounce),
        debounceTime(SEARCH_DEBOUNCE_MS),
      ),
      this.authorQueries.pipe(filter((query) => !query.debounce)),
    )
      .pipe(
        distinctUntilChanged((previous, current) => this.isSameQuery(previous, current)),
        tap(() => {
          this.isSearchPending.set(false);
          this.isLoadingAuthors.set(true);
        }),
        switchMap((query) =>
          this.api
            .getUsers({
              page: query.page,
              limit: AUTHORS_PAGE_LIMIT,
              search: query.searchTerm,
              sortBy: 'name',
              sortOrder: 'asc',
            })
            .pipe(
              map((response) => ({ query, response })),
              catchError(() => of({ query, response: null })),
            ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ query, response }) => {
        this.isLoadingAuthors.set(false);

        if (!response) {
          return;
        }

        this.currentPage.set(response.pagination.page);
        this.pagination.set(response.pagination);
        this.authors.set(this.applyLocalFilters(this.toAuthorCards(response.data), query));
      });
  }

  private requestAuthors(page: number, debounce: boolean): void {
    if (debounce) {
      this.isSearchPending.set(true);
    }

    this.authorQueries.next({
      page,
      searchTerm: this.searchTerm().trim(),
      debounce,
    });
  }

  private isSameQuery(previous: AuthorQuery, current: AuthorQuery): boolean {
    return previous.page === current.page && previous.searchTerm === current.searchTerm;
  }

  private applyLocalFilters(authors: AuthorCard[], query: AuthorQuery): AuthorCard[] {
    const search = query.searchTerm.toLowerCase();

    return authors.filter((author) => {
      return !search || author.searchContent.includes(search);
    });
  }

  private toAuthorCards(users: FrontendUser[]): AuthorCard[] {
    return users
      .filter((user) => user.status !== 'inactive')
      .map((user) => {
        const role = this.displayRole(this.stripHtml(user.designation?.trim() || ''));
        const bio = this.authorDescription(user);
        const articleCount = this.toCount(user.totalArticles);
        const storesCovered = this.toCount(user.totalStores);
        const profilePath = this.expertProfilePath(user.slug);

        return {
          slug: user.slug,
          name: user.name,
          role,
          bio,
          imageUrl: this.assetUrl(user.profileImagePath) || '/images/blog-placeholder.svg',
          articleCount,
          storesCovered,
          profilePath,
          searchContent: this.searchContent(user.name, user.designation ?? ''),
        };
      });
  }

  /**
   * Blank for the default "Author", which every seeded user carries — thirty identical role chips
   * label nothing. A real designation ("Deals Editor") still shows.
   */
  private displayRole(role: string): string {
    return role.toLowerCase() === GENERIC_ROLE ? '' : role;
  }

  private searchContent(name: string, designation: string): string {
    return `${name} ${designation}`.toLowerCase();
  }

  private toCount(value: number | undefined): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }

  private authorDescription(user: FrontendUser): string {
    const description =
      user.shortDescription?.trim() ||
      user.bio?.trim() ||
      'SavingArena author sharing practical savings guidance.';

    return this.stripHtml(description);
  }

  private stripHtml(value: string): string {
    return value
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private expertProfilePath(slug: string): string {
    return `/experts/${slug}`;
  }

  private assetUrl(path: string | undefined): string {
    if (!path) {
      return '';
    }

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const assetBaseUrl = this.apiConfig.assetBaseUrl.replace(/\/$/, '');
    const assetPath = path.startsWith('/') ? path : `/${path}`;

    return `${assetBaseUrl}${assetPath}`;
  }

  private emptyPagination(): ApiPagination {
    return { page: 1, limit: AUTHORS_PAGE_LIMIT, total: this.authors().length, pages: 1 };
  }
}
